import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  TreeDeciduous, ArrowRight, Loader2,
  TreePine, Bug, FileText, Sprout, Compass,
  Users, GraduationCap, Building2, HelpCircle, Info, Lock,
} from 'lucide-react';
import {
  getBeispielFaelle, BEISPIEL_FAELLE, getFbgBeispielFaelle,
  getBeraterBeispielFaelle, getKommuneBeispielFaelle, getUnsicherBeispielFaelle,
} from './demoFaelle';
import { EingabeAssistent } from './EingabeAssistent';

const ICON_MAP = {
  trees: TreePine,
  bug: Bug,
  file: FileText,
  seedling: Sprout,
  compass: Compass,
};

const ZIELGRUPPEN_KEYS = [
  { id: 'waldbesitzer', labelKey: 'schnellcheck.zg_waldbesitzer', icon: TreePine },
  { id: 'fbg', labelKey: 'schnellcheck.zg_fbg', icon: Users },
  { id: 'berater', labelKey: 'schnellcheck.zg_berater', icon: GraduationCap },
  { id: 'kommune', labelKey: 'schnellcheck.zg_kommune', icon: Building2 },
  { id: 'unsicher', labelKey: 'schnellcheck.zg_unsicher', icon: HelpCircle },
];

export function StepEingabe({ freitext, setFreitext, onSubmit, loading, isPublic, zielgruppe, onZielgruppeChange, t }) {
  const [showBeispiele, setShowBeispiele] = useState(!freitext);

  const isFbg = zielgruppe === 'fbg';
  const beispielFaelle = (() => {
    if (!t) return BEISPIEL_FAELLE;
    switch (zielgruppe) {
      case 'fbg': return getFbgBeispielFaelle(t);
      case 'berater': return getBeraterBeispielFaelle(t);
      case 'kommune': return getKommuneBeispielFaelle(t);
      case 'unsicher': return getUnsicherBeispielFaelle(t);
      default: return getBeispielFaelle(t);
    }
  })();

  const handleBeispielClick = (beispiel) => {
    setFreitext(beispiel.freitext);
    setShowBeispiele(false);
  };

  return (
    <div data-testid="step-eingabe">
      <div className="mb-8">
        <h1 className="font-serif text-2xl sm:text-3xl font-bold text-foreground mb-2">
          {isFbg ? t('fbg.eingabeTitle') : isPublic ? t('schnellcheck.title') : t('schnellcheck.subtitle')}
        </h1>
        <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
          {isFbg ? t('fbg.eingabeDesc') : isPublic ? t('schnellcheck.beschreibungPublic') : t('schnellcheck.beschreibungPrivat')}
        </p>
      </div>

      {/* FBG Info-Banner */}
      {isFbg && (
        <div className="mb-6 p-4 rounded-xl border-2 border-blue-200 bg-blue-50/60 flex items-start gap-3" data-testid="fbg-info-banner">
          <div className="p-2 rounded-lg bg-blue-100 shrink-0 mt-0.5">
            <Info className="h-4 w-4 text-blue-700" />
          </div>
          <p className="text-sm text-blue-800 leading-relaxed">{t('fbg.infoBanner')}</p>
        </div>
      )}

      {/* Zielgruppen-Auswahl */}
      {onZielgruppeChange && (
        <div className="mb-6" data-testid="zielgruppen-auswahl">
          <p className="text-sm font-medium text-foreground mb-3">{t('schnellcheck.ichPruefe')}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {ZIELGRUPPEN_KEYS.map((zg) => {
              const Icon = zg.icon;
              const selected = zielgruppe === zg.id;
              return (
                <button
                  key={zg.id}
                  onClick={() => onZielgruppeChange(zg.id)}
                  className={`flex items-center gap-2.5 p-3 rounded-lg border text-left text-sm font-medium transition-all ${
                    selected
                      ? 'border-primary bg-primary/5 text-primary ring-1 ring-primary/20'
                      : 'border-warm-200 bg-white text-foreground hover:border-primary/30 hover:bg-primary/[0.02]'
                  }`}
                  data-testid={`zg-${zg.id}`}
                >
                  <Icon className={`h-4 w-4 shrink-0 ${selected ? 'text-primary' : 'text-muted-foreground'}`} />
                  <span className="truncate">{t(zg.labelKey)}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Beispiel-Fälle */}
      {showBeispiele && (
        <div className="mb-6" data-testid="beispiel-faelle">
          <p className="text-sm font-medium text-muted-foreground mb-3">
            {isFbg ? t('fbg.beispieleTitle') : t('schnellcheck.beispielAuswaehlen')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {beispielFaelle.map((beispiel) => {
              const Icon = ICON_MAP[beispiel.icon] || TreeDeciduous;
              return (
                <button
                  key={beispiel.id}
                  onClick={() => handleBeispielClick(beispiel)}
                  className="text-left p-4 rounded-lg border border-warm-200 bg-white hover:border-primary/40 hover:bg-primary/[0.02] transition-all group"
                  data-testid={`beispiel-${beispiel.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary/8 flex items-center justify-center shrink-0 group-hover:bg-primary/12 transition-colors">
                      <Icon className="h-4.5 w-4.5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground mb-0.5">{beispiel.titel}</p>
                      <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{beispiel.kurz}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {beispiel.tags.map((tag) => (
                          <Badge key={tag} variant="outline" className="text-[10px] px-1.5 py-0 border-warm-200 text-muted-foreground">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-warm-200" />
            <span className="text-xs text-muted-foreground">{t('schnellcheck.oderEigenenFall')}</span>
            <div className="flex-1 h-px bg-warm-200" />
          </div>
        </div>
      )}

      {/* Freitext */}
      <Card className="border-warm-200 shadow-soft mb-6">
        <CardContent className="pt-6">
          <Textarea
            data-testid="freitext-input"
            value={freitext}
            onChange={(e) => { setFreitext(e.target.value); setShowBeispiele(false); }}
            placeholder={isFbg ? t('fbg.placeholder') : t('schnellcheck.placeholder')}
            className="min-h-[140px] text-base resize-none border-warm-200 focus:border-primary focus:ring-primary/20"
          />
          <p className="text-xs text-muted-foreground/70 mt-2 leading-relaxed" data-testid="freitext-mikrohilfe">
            {t('schnellcheck.mikrohilfe')}
          </p>
          <div className="flex items-center justify-between mt-2">
            {!showBeispiele && (
              <button
                onClick={() => { setFreitext(''); setShowBeispiele(true); }}
                className="text-xs text-muted-foreground hover:text-primary transition-colors"
                data-testid="beispiele-zurueck"
              >
                {t('schnellcheck.beispieleAnzeigen')}
              </button>
            )}
            {showBeispiele && <span />}
            <span className="text-xs text-muted-foreground">
              {freitext.length} {t('schnellcheck.zeichen')}
            </span>
          </div>

          {/* KI-Eingabeassistent: Echtzeit-Tipps beim Tippen */}
          {freitext.length >= 5 && (
            <EingabeAssistent
              freitext={freitext}
              onTippKlick={(tipp) => {
                setFreitext(prev => prev.trimEnd() + (prev.endsWith('.') || prev.endsWith('?') || prev.endsWith('!') ? ' ' : '. ') + tipp + ': ');
              }}
              onVorlageKlick={(vorlage) => {
                setFreitext(vorlage);
              }}
              t={t}
            />
          )}
        </CardContent>
      </Card>

      {/* Ergebnis-Vorschau: Was der Nutzer am Ende bekommt */}
      {isPublic && freitext.length < 10 && (
        <div className="mb-6 px-4 py-3 rounded-lg bg-primary/[0.03] border border-primary/10" data-testid="ergebnis-vorschau">
          <p className="text-xs text-gray-600 leading-relaxed mb-1.5">{t('schnellcheck.ergebnisVorschau')}</p>
          <p className="text-[11px] text-gray-400 leading-relaxed">{t('schnellcheck.ergebnisVorschauStruktur')}</p>
        </div>
      )}

      <Button
        onClick={() => onSubmit(freitext)}
        disabled={loading || freitext.trim().length < 10}
        className="w-full sm:w-auto h-12 px-8 text-base font-medium"
        data-testid="analysieren-btn"
      >
        {loading ? (
          <>
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            {t('schnellcheck.wirdAnalysiert')}
          </>
        ) : (
          <>
            <TreeDeciduous className="h-5 w-5 mr-2" />
            {isPublic ? t('schnellcheck.analyseStarten') : t('schnellcheck.analyseStarten')}
            <ArrowRight className="h-4 w-4 ml-2" />
          </>
        )}
      </Button>

      {/* TEIL E: Datenhinweis direkt bei der Eingabe */}
      <p className="text-xs text-muted-foreground mt-3 flex items-start gap-1.5" data-testid="daten-hinweis-eingabe">
        <Lock className="h-3 w-3 mt-0.5 shrink-0 text-muted-foreground/60" />
        Ihre Angaben werden nur zur Einordnung Ihres Falls verwendet und nicht an Dritte weitergegeben.
      </p>
    </div>
  );
}
